import { css, styled } from "styled-components";

import {
  Avatar,
  AvatarButton,
  Icon,
  Menu as MenuBase,
  Stack,
} from "@fidelix/fx-miranda";
import { type Key } from "react-aria-components";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useNavigate } from "react-router-dom";
import { config } from "../config";
import { useCurrentUserQuery } from "../user/user.queries";
import { isGlobalAdmin } from "../helper";
import { useCompaniesQuery } from "../company/company.queries";

export function NavMenu({ id }: { id: string }) {
  const { data: user } = useCurrentUserQuery();
  const { data: companies = [] } = useCompaniesQuery();
  const navigate = useNavigate();

  const items: { id: string; url: string; label: string; name: string }[] = [];

  if (companies.length > 0) {
    // Hide Member menu from list if no company
    items.unshift({
      id: "Member",
      url: "/company",
      label: t`Member`,
      name: user?.fullName || "--",
    });
  }

  if (isGlobalAdmin(user!.permissions)) {
    items.push({
      id: "Admin",
      url: "/manage",
      label: t`Admin`,
      name: "Admin",
    });
  }
  const handleMenuAction = (id: Key) => {
    if (id === "/me") {
      window.open(`${config.ADMIN_URL}/me`, "_blank");
      return;
    }
    navigate(`${id}`);
  };

  return (
    <Menu
      onAction={handleMenuAction}
      trigger={
        <AvatarButton
          name={user?.fullName || ""}
          data-test-id="nav-menu-button"
        />
      }
    >
      <Menu.Section title="Role">
        {items.map((item, index) => {
          const { id: itemId, label, name, url } = item;
          return (
            <ProfileMenuItem
              key={index}
              url={url}
              name={name}
              label={label}
              isSelected={id === itemId}
            />
          );
        })}
      </Menu.Section>
      <Menu.Section title="Pages">
        <Menu.Item id={"/me"} data-test-id="nav-menu-item-logout">
          <Stack axis="x" spacing="xsmall" align="center">
            <Icon icon="accountFilled" size={18} />
            <Trans>My account</Trans>
          </Stack>
        </Menu.Item>
      </Menu.Section>
      <Menu.Separator />
      <Menu.Item href="/logout" data-test-id="nav-menu-item-logout">
        <Stack axis="x" spacing="xsmall" align="center">
          <Icon icon="logOut" size={18} />
          <Trans>Logout</Trans>
        </Stack>
      </Menu.Item>
    </Menu>
  );
}

function ProfileMenuItem({
  label,
  url,
  isSelected,
  name,
}: {
  label: string;
  url: string;
  name: string;
  isSelected: boolean;
}) {
  return (
    <ProfileMenuItemWrapper id={url} $isSelected={isSelected}>
      <Stack axis="x" spacing="xsmall" align="center">
        <Avatar name={name} />
        <span>{label}</span>
      </Stack>
    </ProfileMenuItemWrapper>
  );
}

const ProfileMenuItemWrapper = styled(MenuBase.Item)<{ $isSelected: boolean }>`
  ${(p) =>
    p.$isSelected &&
    css`
      background-color: ${p.theme.colors.primary};
      color: ${p.theme.colors.white};

      &[data-hovered="true"] {
        background-color: ${p.theme.colors.primary};
      }
    `}
`;

const Menu = styled(MenuBase)`
  min-width: 250px;
`;
